import _ from 'lodash';
import React, { useState, useCallback, useMemo, useEffect } from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FIELD_PADDING } from '../SettlePayment/const';

const TYPE_LENGTH = 2;

const BookingsAutocomplete = ({
	value,
	onChange,
	fetchOptions,
	getOptionLabel = (opt) => opt?.label || '',
	isOptionEqualToValue = (opt, val) => {
		if (!opt || !val) return false;
		if (opt.id != null && val.id != null) return opt.id === val.id;
		return (opt.label || '') === (val.label || '');
	},
	placeholder = '',
	id = '',
	mapValue = (opt) => opt,
}) => {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);

	const loadOptions = useCallback(
		(query) => {
			if (!query || query.length < TYPE_LENGTH) {
				setOptions([]);
				return;
			}

			setLoading(true);
			fetchOptions(query)
				.then((res) => setOptions(res || []))
				.catch((err) => {
					console.error('Autocomplete fetch error', err);
					setOptions([]);
				})
				.finally(() => setLoading(false));
		},
		[fetchOptions],
	);

	const debouncedLoad = useMemo(() => _.debounce(loadOptions, 500), [loadOptions]);

	useEffect(() => {
		return () => debouncedLoad.cancel();
	}, [debouncedLoad]);


	const handleInputChange = (event, newInput) => {
		const newInputValue = newInput ? newInput.replace(/ +/g,' ') : '';
		setInputValue(newInputValue);

		if (!newInputValue || newInputValue.length < 2) {
			// below threshold: no loading, no options
			debouncedLoad.cancel();
			setLoading(false);
			setOptions([]);
			return;
		}

		// user typed enough: show spinner immediately
		setLoading(true);
		debouncedLoad(newInputValue);
	};

	const noOptionsText = () => {
		if (inputValue.length >= TYPE_LENGTH && loading) {
			return t('Loading');
		}

		return inputValue.length < TYPE_LENGTH ? t('Bookings.Filter.PleaseTypeTheHotelName') : t('Bookings.Filter.NoResults');
	};

	return (
		<Autocomplete
			id={id}
			popupIcon={null}
			options={options}
			getOptionLabel={getOptionLabel}
			value={value || null}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			onChange={(event, newValue) => onChange?.(mapValue(newValue))}
			isOptionEqualToValue={isOptionEqualToValue}
			loading={loading}
			loadingText={t('Loading')}
			noOptionsText={noOptionsText()}
			size="small"
			disableClearable={false}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					size="small"
					placeholder={placeholder}
					fullWidth
					sx={FIELD_PADDING}
					InputProps={{
						...params.InputProps,
						endAdornment: value ? (
							<InputAdornment position="end">
								<IconButton onClick={() => onChange?.(mapValue(null))} disableRipple>
									<CloseIcon />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>
			)}
		/>
	);
};

export default BookingsAutocomplete;
